import {
  Box,
  Button,
  Callout,
  DATE_FORMAT,
  HighlightIcon,
  PanelArea,
  PanelBody,
  PanelContent,
  PanelFooter,
  PanelNavigation,
  SwitchField,
  Tag,
} from '@dev-spendesk/grapes';
import queryString from 'query-string';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';

import { QuerySuspense } from 'common/components/QuerySuspense';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useNotifications } from 'modules/app/notifications';
import { useDeletePayableRulesMutation } from 'modules/bookkeep/settings/integrations/hooks/useDeletePayableRulesMutation';
import { getPayableRulesQueryKey } from 'modules/bookkeep/settings/integrations/hooks/useGetPayableRules';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { sortCostCenters } from 'modules/company/cost-centers/utils';
import { type EnrichedPayableRule } from 'modules/payable';
import { UserAvatar } from 'modules/users/components';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor, routes } from 'src/core/constants/routes';
import { useDesignatedCustomFieldQuery } from 'src/core/modules/bookkeep/payables/containers/PayableAccountingSectionContainer/hooks/useDesignatedCustomFieldQuery';
import { getCustomFieldsWithArchiveStatus } from 'src/core/modules/payable';

import { PayableRuleDeleteConfirmationModal } from './PayableRuleDeleteConfirmationModal';
import {
  getPayableRulesMCRLimitQueryKey,
  type MCRLimit,
} from '../../../hooks/useGetPayableRulesMCRLimit';
import { PayableRuleCreate } from '../PayableRuleCreate';
import { generateConditionBox, getIconVariantFromName } from '../utils';
import { useUpdatePayableRulesStateFromTable } from '../utils/useUpdatePayableRulesStateFromTable';

type Props = {
  payableRule: EnrichedPayableRule;
  mcrLimit: MCRLimit;
};

export const PayableRuleDetails = ({ payableRule, mcrLimit }: Props) => {
  const history = useHistory();
  const company = useCompany();
  const queryClient = useQueryClient();
  const location = useLocation();
  const designatedCustomFieldQueryState = useDesignatedCustomFieldQuery();
  const designatedCustomFieldId = unwrapQuery(
    designatedCustomFieldQueryState,
  )?.customFieldId;

  const search = queryString.parse(location.search);
  const [editMode, setEditMode] = useState(search?.edit === 'true');
  const { localeFormat, t } = useTranslation('global');
  const { dangerNotif, successNotif } = useNotifications();
  const costCenterQueryState = useCostCentersQuery();
  const { mutateAsync: deletePayableRules } = useDeletePayableRulesMutation();
  const [isDeleteConfirmationModalStateOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);

  const updatePayableRulesStateFromTable = useUpdatePayableRulesStateFromTable(
    company.id,
  );

  if (editMode) {
    return (
      <QuerySuspense
        queryState={costCenterQueryState}
        loading={null}
        fallback={() => null}
      >
        {(costCenters) => (
          <PanelContent DO_NOT_USE_isSidePanel>
            <PayableRuleCreate
              costCenters={sortCostCenters(costCenters)}
              mcrLimit={mcrLimit}
              payableRule={payableRule}
              onBack={() => setEditMode(false)}
            />
          </PanelContent>
        )}
      </QuerySuspense>
    );
  }

  return (
    <>
      <PayableRuleDeleteConfirmationModal
        isOpen={isDeleteConfirmationModalStateOpen}
        setIsOpen={setIsDeleteConfirmationModalOpen}
        onDelete={async () => {
          try {
            await deletePayableRules({
              payableRuleIds: [payableRule.id],
            });
            queryClient.invalidateQueries(getPayableRulesQueryKey);
            queryClient.invalidateQueries(getPayableRulesMCRLimitQueryKey);
            successNotif(
              t(
                'bookkeep.integrations.settings.payableRules.details.ruleDeleted',
                {
                  ruleName: payableRule.name,
                },
              ),
            );

            history.push(
              routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
                company: company.id,
              }),
            );
          } catch {
            dangerNotif(t('misc.somethingWrong'));
          }
        }}
        numberOfRulesToBeDeleted={1}
      />
      <PanelContent DO_NOT_USE_isSidePanel>
        <PanelBody>
          <PanelNavigation
            onClose={() => {
              history.push(
                routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
                  company: company.id,
                }),
              );
            }}
          >
            {(titleId) => (
              <h2 className="heading-xl" id={titleId}>
                {t('bookkeep.integrations.settings.payableRules.details.title')}
              </h2>
            )}
          </PanelNavigation>
          <PanelArea>
            <div className="mb-12 flex flex-col gap-12">
              {payableRule.problems.length > 0 && (
                <Callout variant="warning" title={payableRule.problems[0]} />
              )}
              <Box
                variant="secondary"
                className="flex items-center justify-between p-16"
              >
                <div className="flex items-center gap-12">
                  <HighlightIcon
                    size={40}
                    name={payableRule.icon}
                    variant={getIconVariantFromName(payableRule.icon)}
                  />
                  <span className="title-l">{payableRule.displayName}</span>
                </div>
                <SwitchField
                  label=""
                  className="items-center"
                  isChecked={payableRule.state === 'published'}
                  onChange={async () => {
                    await updatePayableRulesStateFromTable(
                      [payableRule.id],
                      payableRule.state === 'published'
                        ? 'unpublish'
                        : 'publish',
                    );
                  }}
                />
              </Box>
              <Box
                variant="secondary"
                className="flex items-center justify-between p-16"
              >
                <div className="flex items-center gap-12">
                  <UserAvatar
                    size={40}
                    id={payableRule.createdByUser?.id ?? 'userId'}
                    name={
                      payableRule.createdByUser?.fullname ??
                      t(
                        'bookkeep.integrations.settings.payableRules.defaultUserName',
                      )
                    }
                  />
                  <span className="title-l">
                    {payableRule.createdByUser?.fullname ??
                      t(
                        'bookkeep.integrations.settings.payableRules.defaultUserNameDescription',
                      )}
                  </span>
                </div>
              </Box>
              <div className="flex items-center gap-12">
                <UserAvatar
                  size={24}
                  id={payableRule.updatedByUser?.id ?? 'userId'}
                  name={
                    payableRule.updatedByUser?.fullname ??
                    t(
                      'bookkeep.integrations.settings.payableRules.defaultUserName',
                    )
                  }
                />
                <span className="text-content-secondary-bg-primary body-m">
                  {t(
                    'bookkeep.integrations.settings.payableRules.details.editedLabel',
                    {
                      userName:
                        payableRule.updatedByUser?.fullname ??
                        t(
                          'bookkeep.integrations.settings.payableRules.defaultUserNameDescription',
                        ),
                      date: payableRule.updatedAt
                        ? localeFormat(
                            new Date(payableRule.updatedAt),
                            DATE_FORMAT.MEDIUM,
                          )
                        : t('misc.na'),
                    },
                  )}
                </span>
              </div>
              <div className="separator my-12" />
              <div className="flex items-center gap-12">
                <HighlightIcon name="funnel" size={40} variant="blue" />
                <span className="title-l">
                  {t(
                    'bookkeep.integrations.settings.payableRules.conditionsTitle',
                  )}
                </span>
              </div>
              {payableRule.conditions.map((condition) =>
                generateConditionBox(
                  condition,
                  company,
                  t,
                  designatedCustomFieldId,
                ),
              )}
              <div className="my-12 ml-16 h-[40px] w-[1px] border border-l-[0px] border-r-[1px] border-solid border-border-default" />
              <div className="flex items-center gap-12">
                <HighlightIcon name="circle-play" size={40} variant="lemon" />
                <span className="title-l">
                  {t(
                    'bookkeep.integrations.settings.payableRules.outputsTitle',
                  )}
                </span>
              </div>
              {generateOutputBoxes(
                payableRule.outputs,
                t,
                designatedCustomFieldId,
              )}
              {payableRule.stats && (
                <>
                  <div className="separator my-12" />
                  <div className="flex flex-col gap-20">
                    <div className="flex items-center gap-12">
                      <HighlightIcon
                        name="chart-pie"
                        size={40}
                        variant="pink"
                      />
                      <span className="title-l">
                        {t(
                          'bookkeep.integrations.settings.payableRules.statsTitle',
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-8">
                      {[
                        {
                          label: t(
                            'bookkeep.integrations.settings.payableRules.details.stats.payablesMatched',
                          ),
                          value: payableRule.stats.payablesMatched,
                        },
                        {
                          label: t(
                            'bookkeep.integrations.settings.payableRules.details.stats.payablesPrepared',
                          ),
                          value: payableRule.stats.payablesPrepared,
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="flex items-center justify-between self-stretch"
                        >
                          <span className="flex text-content-secondary-bg-primary body-m">
                            {item.label}
                          </span>
                          <span className="flex items-center justify-end gap-4">
                            {item.value}
                          </span>
                        </div>
                      ))}
                      <span className="italic text-content-secondary-bg-primary body-s">
                        {t(
                          'bookkeep.integrations.settings.payableRules.details.stats.sinceLastRuleEdit',
                        )}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </PanelArea>
        </PanelBody>
        <PanelFooter>
          <div className="flex items-center gap-16">
            <Button
              text={t(
                'bookkeep.integrations.settings.payableRules.details.delete',
              )}
              className="w-full"
              variant="secondaryAlert"
              onClick={() => {
                setIsDeleteConfirmationModalOpen(true);
              }}
            />
            <Button
              variant="secondaryNeutral"
              className="w-full"
              text={t(
                'bookkeep.integrations.settings.payableRules.details.edit',
              )}
              onClick={() => {
                setEditMode(!editMode);
              }}
            />
          </div>
        </PanelFooter>
      </PanelContent>
    </>
  );
};

const generateOutputBoxes = (
  outputs: EnrichedPayableRule['outputs'],
  translator: TGlobalFunctionTyped,
  designatedCustomFieldId?: string,
) => {
  const expenseAccount =
    typeof outputs.expenseAccount === 'object'
      ? outputs.expenseAccount
      : undefined;
  const vatAccount =
    typeof outputs.vatAccount === 'object' ? outputs.vatAccount : undefined;
  const costCenter =
    typeof outputs.costCenter === 'object' ? outputs.costCenter : undefined;
  const customFields =
    (outputs.customField &&
      getCustomFieldsWithArchiveStatus(outputs.customField)) ||
    [];

  return (
    <div className="flex flex-col gap-12">
      {expenseAccount && (
        <Box variant="secondary" className="p-16">
          <div className="flex flex-col gap-4">
            <span>
              {translator(
                'bookkeep.integrations.settings.payableRules.details.setExpenseAccount',
              )}
            </span>
            <div className="flex truncate">
              {!expenseAccount.isArchived && (
                <Tag variant="white">{expenseAccount.name}</Tag>
              )}
              {expenseAccount.isArchived && (
                <Tag
                  iconName="triangle-warning"
                  variant="warning"
                  className="crossed-out"
                >
                  {expenseAccount.name}
                </Tag>
              )}
            </div>
          </div>
        </Box>
      )}
      {vatAccount && (
        <Box variant="secondary" className="p-16">
          <div className="flex flex-col gap-4">
            <span>
              {translator(
                'bookkeep.integrations.settings.payableRules.details.setVatAccount',
              )}
            </span>
            <div className="flex truncate">
              {!vatAccount.isArchived && (
                <Tag variant="white" className="w-auto">
                  {vatAccount.name}
                </Tag>
              )}
              {vatAccount.isArchived && (
                <Tag
                  iconName="triangle-warning"
                  variant="warning"
                  className="crossed-out"
                >
                  {vatAccount.name}
                </Tag>
              )}
            </div>
          </div>
        </Box>
      )}
      {costCenter && (
        <Box variant="secondary" className="p-16">
          <div className="flex flex-col gap-4">
            <span>
              {translator(
                'bookkeep.integrations.settings.payableRules.details.setCostCenter',
              )}
            </span>
            <div className="flex truncate">
              {!costCenter.isDeleted && (
                <Tag variant="white">{costCenter.name}</Tag>
              )}
              {costCenter.isDeleted && (
                <Tag
                  iconName="triangle-warning"
                  variant="warning"
                  className="crossed-out"
                >
                  {costCenter.name}
                </Tag>
              )}
            </div>
          </div>
        </Box>
      )}
      {customFields &&
        customFields.map((customField) => {
          let displayValue = customField.name;
          if (customField.type === 'Boolean') {
            displayValue =
              customField.value === 'true'
                ? translator('misc.yes')
                : translator('misc.no');
          }
          const setCustomFieldValue =
            customField.id === designatedCustomFieldId
              ? translator('misc.expenseCategory').toLowerCase()
              : customField.cfName;
          return (
            <Box
              variant="secondary"
              key={String(customField.name)}
              className="p-16"
            >
              <div className="flex flex-col gap-4">
                <span>
                  {translator(
                    'bookkeep.integrations.settings.payableRules.details.setCustomField',
                    {
                      customField: setCustomFieldValue,
                    },
                  )}
                </span>
                <div className="flex truncate">
                  {customField.isArchived ? (
                    <Tag
                      iconName="triangle-warning"
                      variant="warning"
                      className="crossed-out"
                    >
                      {displayValue}
                    </Tag>
                  ) : (
                    <Tag variant="white">{displayValue}</Tag>
                  )}
                </div>
              </div>
            </Box>
          );
        })}
    </div>
  );
};
